import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowUpRight, MapPin } from 'lucide-react';
import publicApi, { normalizeEventForCard, resolveMediaUrl } from '../lib/publicApi';
import { useI18n } from '../contexts/I18nContext';

const badgeColors = {
  coral: 'bg-[#E5614C] text-white',
  teal: 'bg-[#5DCAB5] text-white',
  yellow: 'bg-[#FCD968] text-[#2A1B1A]',
};

// Diverse fallback imagery — used only when a CMS event is missing its own photo.
const DIVERSE_FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=900&h=600&fit=crop',
  'https://images.unsplash.com/photo-1529390079861-591de354faf5?w=900&h=600&fit=crop',
  'https://images.unsplash.com/photo-1531058020387-3be344556be6?w=900&h=600&fit=crop',
  'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=900&h=600&fit=crop',
  'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=900&h=600&fit=crop',
  'https://images.unsplash.com/photo-1525362081669-2b476bb628c3?w=900&h=600&fit=crop',
];

const BADGE_ROTATE = ['coral', 'teal', 'yellow'];

// Fall + Winter 2026 static event cards — shown when the CMS has no upcoming
// events published yet. Copy is pulled from i18n via `home.evt.<slot>.*` so
// each language reads the details in the visitor's tongue.
const FALLBACK_EVENTS = [
  {
    slot: 'openmic',
    day: '1st',
    month: 'THU',
    badge: 'coral',
    icon: '🎤',
    image: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=900&h=600&fit=crop',
  },
  {
    slot: 'fallfest',
    day: '01',
    month: 'OCT',
    badge: 'yellow',
    icon: '🍂',
    image: 'https://images.unsplash.com/photo-1477414348463-c0eb7f1359b6?w=900&h=600&fit=crop',
  },
  {
    slot: 'holiday',
    day: '20',
    month: 'DEC',
    badge: 'teal',
    icon: '🎄',
    image: 'https://images.unsplash.com/photo-1512389142860-9c449e58a543?w=900&h=600&fit=crop',
  },
];

function SkeletonCard() {
  return (
    <div className="bg-white rounded-[24px] overflow-hidden border border-[#2A1B1A]/8 animate-pulse" data-testid="event-skeleton">
      <div className="h-[240px] bg-[#FAF3EE]" />
      <div className="p-6 space-y-3">
        <div className="h-3 w-24 bg-[#FAF3EE] rounded" />
        <div className="h-6 w-3/4 bg-[#FAF3EE] rounded" />
        <div className="h-4 w-full bg-[#FAF3EE] rounded" />
        <div className="h-4 w-5/6 bg-[#FAF3EE] rounded" />
      </div>
    </div>
  );
}

export default function Events() {
  const { t } = useI18n();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    // Pull ALL upcoming published events (no category filter). If the CMS
    // returns nothing, we fall back to the seasonal static cards below.
    publicApi
      .get('/events', {
        params: { status: 'published', upcoming: true, limit: 10 },
      })
      .then((res) => {
        if (!mounted) return;
        const normalized = (res.data || [])
          .map(normalizeEventForCard)
          .slice(0, 3)
          .map((ev, idx) => {
            const usingResolved = resolveMediaUrl((res.data || [])[idx]?.featured_image);
            const finalImage = usingResolved
              ? ev.image
              : DIVERSE_FALLBACK_IMAGES[idx % DIVERSE_FALLBACK_IMAGES.length];
            return { ...ev, image: finalImage, badge: BADGE_ROTATE[idx % BADGE_ROTATE.length] };
          });
        setEvents(normalized);
      })
      .catch(() => {
        if (mounted) setEvents([]);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const showFallback = !loading && events.length === 0;

  return (
    <section id="events" className="py-20 lg:py-28 bg-white" data-testid="home-events-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[12px]">
            <span className="h-[2px] w-8 bg-[#E5614C]" /> {t('home.events.eyebrow')}
          </div>
          <h2 className="font-serif-display text-[40px] sm:text-[48px] lg:text-[56px] leading-[1.05] font-bold text-[#2A1B1A] mt-4">
            {t('home.events.title1')} <span className="italic text-[#E5614C]">{t('home.events.title2')}</span>
          </h2>
          <p className="mt-4 text-[16px] text-[#2A1B1A]/70">
            {t('home.events.desc')}
          </p>
        </div>

        {loading ? (
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {[0, 1, 2].map((i) => <SkeletonCard key={i} />)}
          </div>
        ) : showFallback ? (
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="home-events-fallback">
            {FALLBACK_EVENTS.map((e) => (
              <article
                key={e.slot}
                data-testid={`event-fallback-${e.slot}`}
                className="group bg-white rounded-[24px] overflow-hidden border border-[#2A1B1A]/8 hover:border-transparent hover:shadow-[0_30px_70px_-30px_rgba(42,27,26,0.35)] hover:-translate-y-1.5 transition-[transform,box-shadow,border-color] duration-300 flex flex-col"
              >
                <div className="relative h-[240px] overflow-hidden">
                  <img
                    src={e.image}
                    alt={t(`home.evt.${e.slot}.title`)}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1.2s]"
                  />
                  <div className={`absolute top-4 left-4 px-3.5 py-2 rounded-xl font-bold ${badgeColors[e.badge]} text-center leading-tight shadow-lg`}>
                    <div className="text-[20px] leading-none">{e.day}</div>
                    <div className="text-[10px] mt-0.5 tracking-wider">{e.month}</div>
                  </div>
                  <div className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/95 flex items-center justify-center text-[18px] shadow-md" aria-hidden="true">
                    {e.icon}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#2A1B1A]/55">
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> {t(`home.evt.${e.slot}.when`)}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" /> {t(`home.evt.${e.slot}.where`)}
                    </span>
                  </div>
                  <h3 className="mt-3 font-serif-display text-[24px] font-bold text-[#2A1B1A]">
                    {t(`home.evt.${e.slot}.title`)}
                  </h3>
                  <p className="mt-2.5 text-[14px] text-[#2A1B1A]/70 leading-relaxed flex-1">
                    {t(`home.evt.${e.slot}.desc`)}
                  </p>
                  <Link
                    to="/contact"
                    className="mt-5 inline-flex items-center gap-1.5 text-[#E5614C] font-semibold text-sm hover:gap-2.5 transition-[gap] self-start"
                  >
                    {t(`home.evt.${e.slot}.cta`)} <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {events.map((e) => (
              <Link
                key={e.id}
                to="/summer-series"
                data-testid={`event-card-${e.id}`}
                className="group bg-white rounded-[24px] overflow-hidden border border-[#2A1B1A]/8 hover:border-transparent hover:shadow-[0_30px_70px_-30px_rgba(42,27,26,0.35)] hover:-translate-y-1.5 transition-[transform,box-shadow,border-color] duration-300 block"
              >
                <div className="relative h-[240px] overflow-hidden">
                  <img
                    src={e.image}
                    alt={e.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1.2s]"
                    loading="lazy"
                  />
                  <div className={`absolute top-4 left-4 px-3.5 py-2 rounded-xl font-bold ${badgeColors[e.badge] || badgeColors.coral} text-center leading-tight shadow-lg`}>
                    <div className="text-[20px] leading-none">{e.day}</div>
                    <div className="text-[10px] mt-0.5 tracking-wider">{e.month}</div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#2A1B1A]/55">
                    {e.time && <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {e.time}</span>}
                    {e.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {e.location}</span>}
                  </div>
                  <h3 className="mt-3 font-serif-display text-[24px] font-bold text-[#2A1B1A] group-hover:text-[#E5614C] transition-colors">
                    {e.title}
                  </h3>
                  <p className="mt-2.5 text-[14px] text-[#2A1B1A]/65 leading-relaxed line-clamp-3">
                    {e.desc}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-[#E5614C] font-semibold text-sm group-hover:gap-2.5 transition-[gap]">
                    {t('home.events.cta')} <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
